const APP_ID = 'vintage-slot-frontend';
const BALANCE_SYNC_NAMESPACE = 'parlaygames.balance-sync';
const BRIDGE_READY_TYPE = 'bridge:ready';
const BALANCE_CHANGED_TYPE = 'balance:changed';
const BRIDGE_IFRAME_ID = 'parlaygames-balance-sync-bridge';
const BRIDGE_CONFIG_PATH = '/balance-sync.json';

export type BalanceChangeReason = 'local-action' | 'passive-refresh';

type BridgeReadyMessage = {
    namespace: typeof BALANCE_SYNC_NAMESPACE;
    type: typeof BRIDGE_READY_TYPE;
};

type BalanceChangeMessage = {
    namespace: typeof BALANCE_SYNC_NAMESPACE;
    type: typeof BALANCE_CHANGED_TYPE;
    sourceId: string;
    appId: string;
    reason?: BalanceChangeReason;
    timestamp?: number;
};

type BalanceSyncConfig = {
    iframeBridgeUrl?: string;
};

let iframe: HTMLIFrameElement | null = null;
let externalRefreshDepth = 0;
let isBridgeReady = false;
let queuedMessages: BalanceChangeMessage[] = [];
let removeWindowMessageListener: (() => void) | null = null;
let bridgeUrl = '';
let bridgeOrigin = '';
let bridgeConfigPromise: Promise<string> | null = null;
const listeners = new Set<(message: BalanceChangeMessage) => void>();

const createSourceId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `vintage-slot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const sourceId = createSourceId();

const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const isBridgeReadyMessage = (value: unknown): value is BridgeReadyMessage => {
    return (
        isObject(value) &&
        value.namespace === BALANCE_SYNC_NAMESPACE &&
        value.type === BRIDGE_READY_TYPE
    );
};

const isBalanceChangedMessage = (
    value: unknown
): value is BalanceChangeMessage => {
    return (
        isObject(value) &&
        value.namespace === BALANCE_SYNC_NAMESPACE &&
        value.type === BALANCE_CHANGED_TYPE &&
        typeof value.sourceId === 'string' &&
        value.sourceId.length > 0 &&
        typeof value.appId === 'string' &&
        value.appId.length > 0 &&
        (value.reason === undefined ||
            value.reason === 'local-action' ||
            value.reason === 'passive-refresh') &&
        (value.timestamp === undefined || typeof value.timestamp === 'number')
    );
};

const getBridgeWindow = () => {
    return iframe?.contentWindow || null;
};

const setBridgeUrl = (nextBridgeUrl: string) => {
    bridgeUrl = nextBridgeUrl;

    if (!bridgeUrl) {
        bridgeOrigin = '';
        return;
    }

    try {
        bridgeOrigin = new URL(bridgeUrl, window.location.href).origin;
    } catch (_error) {
        bridgeOrigin = '';
    }
};

const loadBridgeUrl = async () => {
    if (bridgeUrl) {
        return bridgeUrl;
    }

    if (!bridgeConfigPromise) {
        bridgeConfigPromise = fetch(BRIDGE_CONFIG_PATH, {
            headers: {
                'Cache-Control': 'no-cache',
            },
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch ${BRIDGE_CONFIG_PATH}`);
                }

                const config: BalanceSyncConfig = await response.json();
                return typeof config.iframeBridgeUrl === 'string'
                    ? config.iframeBridgeUrl
                    : '';
            })
            .catch(() => '')
            .then((resolvedBridgeUrl) => {
                setBridgeUrl(resolvedBridgeUrl);
                return bridgeUrl;
            });
    }

    return bridgeConfigPromise;
};

const flushQueuedMessages = () => {
    const bridgeWindow = getBridgeWindow();

    if (!bridgeWindow || !isBridgeReady || !bridgeOrigin) {
        return;
    }

    for (const message of queuedMessages) {
        bridgeWindow.postMessage(message, bridgeOrigin);
    }

    queuedMessages = [];
};

const ensureIframe = () => {
    if (typeof document === 'undefined' || !bridgeUrl) {
        return null;
    }

    if (iframe?.isConnected) {
        return iframe;
    }

    const existingIframe = document.getElementById(
        BRIDGE_IFRAME_ID
    ) as HTMLIFrameElement | null;

    if (existingIframe) {
        iframe = existingIframe;
        return iframe;
    }

    iframe = document.createElement('iframe');
    iframe.id = BRIDGE_IFRAME_ID;
    iframe.src = bridgeUrl;
    iframe.title = 'Parlaygames Balance Sync Bridge';
    iframe.tabIndex = -1;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.display = 'none';

    document.body.appendChild(iframe);

    return iframe;
};

const cleanupIframe = () => {
    if (iframe?.parentNode) {
        iframe.parentNode.removeChild(iframe);
    }

    iframe = null;
};

const handleMessage = (event: MessageEvent<unknown>) => {
    if (event.origin !== bridgeOrigin || event.source !== getBridgeWindow()) {
        return;
    }

    const { data } = event;

    if (isBridgeReadyMessage(data)) {
        isBridgeReady = true;
        flushQueuedMessages();
        return;
    }

    if (!isBalanceChangedMessage(data) || data.sourceId === sourceId) {
        return;
    }

    for (const listener of listeners) {
        listener(data);
    }
};

const ensureWindowMessageListener = () => {
    if (typeof window === 'undefined' || removeWindowMessageListener) {
        return;
    }

    window.addEventListener('message', handleMessage);
    removeWindowMessageListener = () => {
        window.removeEventListener('message', handleMessage);
    };
};

const initializeBridge = async () => {
    const resolvedBridgeUrl = await loadBridgeUrl();

    if (!resolvedBridgeUrl || !bridgeOrigin) {
        return;
    }

    ensureWindowMessageListener();
    ensureIframe();
};

export const startExternalBalanceRefresh = () => {
    externalRefreshDepth += 1;
};

export const stopExternalBalanceRefresh = () => {
    externalRefreshDepth = Math.max(0, externalRefreshDepth - 1);
};

export const postBalanceChange = (reason: BalanceChangeReason) => {
    if (externalRefreshDepth > 0) {
        return;
    }

    const message: BalanceChangeMessage = {
        namespace: BALANCE_SYNC_NAMESPACE,
        type: BALANCE_CHANGED_TYPE,
        sourceId,
        appId: APP_ID,
        reason,
        timestamp: Date.now(),
    };

    const bridgeWindow = getBridgeWindow();

    if (!bridgeWindow || !isBridgeReady) {
        queuedMessages.push(message);
        void initializeBridge();
        return;
    }

    bridgeWindow.postMessage(message, bridgeOrigin);
};

export const addBalanceChangeListener = (
    listener: (message: BalanceChangeMessage) => void
) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {};
    }

    listeners.add(listener);
    void initializeBridge();

    return () => {
        listeners.delete(listener);
    };
};

export const closeBalanceSyncChannel = () => {
    removeWindowMessageListener?.();
    removeWindowMessageListener = null;
    isBridgeReady = false;
    queuedMessages = [];
    listeners.clear();
    cleanupIframe();
};
